import React from "react";

import { injectIntl, IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/Accordion";
import Security from "@/icons/__generated__/Security/Security24";
import { IFaqs } from "~/types/api-types";

import { getFormattedTime } from "../../../utils/getFormattedTime";
import {
  eventFaqDefinition,
  miscEventFaqDefinitions,
  category_labels,
  formattedMiscEventFaqDefinitions,
} from "../data/event-faq-definitions";
import {
  eventRulesSelected,
  eventRulesExplicitlyProhibited,
} from "../data/event-faq-display-sections";

import { analyticEventOnClick } from "./faq-utils";

interface EventRulesProps {
  faqData: IFaqs;
  intl: IntlShape;
  userID: string;
  listingID: string;
}

interface EventRuleOptions {
  has_quiet_hours: string;
  loud_music_ends_by: string;
  is_glitter_allowed: string;
  is_hired_security_required: string;
  are_open_flames_allowed: string;
  are_pets_allowed: string;
  is_smoking_allowed: string;
  are_ticketed_events_allowed: string;
  other_event_rules: string;
  hours_adjusted_upon_request: string;
  has_no_flexible_hours: string;
}

const EventRules: React.FC<EventRulesProps> = ({
  faqData,
  intl,
  userID,
  listingID,
}) => {
  const { has_quiet_hours: hasQuietHours } = faqData;

  const hasValidEndTime =
    hasQuietHours && typeof faqData.loud_music_ends_by === "object";
  const hasFlexibleHours = faqData["has_flexible_hours"];
  const selectedEventRules = eventRulesSelected(
    faqData,
  ) as (keyof EventRuleOptions)[];
  const prohibitedEventRules = eventRulesExplicitlyProhibited(
    faqData,
  ) as (keyof EventRuleOptions)[];
  const hasOtherEventRules = faqData["other_event_rules"];
  const hasEventRules =
    selectedEventRules.length > 0 ||
    prohibitedEventRules.length > 0 ||
    hasOtherEventRules;

  const label = intl.formatMessage(category_labels.rules);

  const findProhobitedRules = (rule: string) => {
    return eventFaqDefinition.event_rules?.[rule as keyof EventRuleOptions];
  };

  const hours =
    faqData["has_flexible_hours"] === "hours_adjusted_upon_request"
      ? intl.formatMessage(
          eventFaqDefinition.event_rules?.hours_adjusted_upon_request || {
            id: "",
            defaultMessage: "",
          },
        )
      : intl.formatMessage(
          eventFaqDefinition.event_rules?.has_no_flexible_hours || {
            id: "",
            defaultMessage: "",
          },
        );

  return (
    <div className="EventFAQ">
      <Accordion
        type="multiple"
        onValueChange={() => {
          analyticEventOnClick(userID, listingID, "Event Rules");
        }}
      >
        <AccordionItem value="event-rules-section">
          <AccordionTrigger className="tw-py-24">
            <div className="tw-flex tw-gap-12 tw-items-center">
              <Security />
              <Text element="h3" typeface="headline-1-m">
                {label}
              </Text>
            </div>
          </AccordionTrigger>
          <AccordionContent className="tw-ml-36 tw-pb-24">
            {hasValidEndTime && (
              <>
                <div className="tw-space-y-8">
                  <Text typeface="body-2-m" element="p">
                    {intl.formatMessage(
                      miscEventFaqDefinitions.quiet_hours_header,
                    )}
                  </Text>
                  <Text element="p">
                    {intl.formatMessage(
                      eventFaqDefinition.event_rules?.loud_music_ends_by || {
                        id: "",
                        defaultMessage: "",
                      },
                    )}
                    &nbsp;
                    {faqData.loud_music_ends_by &&
                      getFormattedTime(faqData.loud_music_ends_by, intl)}
                  </Text>
                  <Text className="tw-text-gray-600" element="p">
                    {formattedMiscEventFaqDefinitions.loud_music_warning}
                  </Text>
                </div>
                {(hasFlexibleHours || hasEventRules) && (
                  <hr className="tw-border-gray-200 tw-my-16" />
                )}
              </>
            )}
            {hasFlexibleHours && (
              <>
                <div className="tw-space-y-8">
                  <Text typeface="body-2-m" element="p">
                    {intl.formatMessage(miscEventFaqDefinitions.flexible_hours)}
                  </Text>
                  <Text element="p">{hours}</Text>
                </div>
                {hasEventRules && (
                  <hr className="tw-border-gray-200 tw-my-16" />
                )}
              </>
            )}
            {hasEventRules && (
              <div className="tw-space-y-8">
                <Text typeface="body-2-m" element="p">
                  {intl.formatMessage(
                    miscEventFaqDefinitions.event_rules_header,
                  )}
                </Text>
                {selectedEventRules.length > 0 &&
                  selectedEventRules.map((rule, key) => (
                    <Text element="p" key={key}>
                      {intl.formatMessage(
                        eventFaqDefinition.event_rules?.[rule] || {
                          id: "",
                          defaultMessage: "",
                        },
                      )}
                    </Text>
                  ))}
                {hasOtherEventRules && (
                  <Text element="p">{faqData.other_event_rules}</Text>
                )}
                {prohibitedEventRules.length > 0 &&
                  prohibitedEventRules.map((rule, key) => (
                    <Text
                      className="tw-text-gray-600 tw-line-through"
                      element="p"
                      key={key}
                    >
                      {intl.formatMessage(
                        findProhobitedRules(rule) || {
                          id: "",
                          defaultMessage: "",
                        },
                      )}
                    </Text>
                  ))}
                {faqData.are_ticketed_events_allowed && (
                  <div className="tw-p-8 tw-bg-gray-100">
                    <Text
                      typeface="body-1"
                      className="tw-text-gray-600"
                      element="p"
                    >
                      {formattedMiscEventFaqDefinitions.residential_warning}
                    </Text>
                  </div>
                )}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default injectIntl(EventRules);
